import {
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Clock,
  FileClock,
  ShieldAlert,
  Trophy,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";

import type { CloseReasonCategory, ManagerDealListItem } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FeedCard, type FeedTone } from "@/components/ui/feed-card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatAud } from "@/lib/currency";
import { CLOSE_REASON_LABELS } from "@/lib/deal-labels";

import {
  daysUntilExpiry,
  expiryNoticeCanSnooze,
  formatReviewSlaCountdown,
  formatSlaCountdown,
  type ManagerFeedItem,
  type ManagerFeedItemKind,
} from "./sla";

const MAX_FEED_ITEMS = 5;

// CHP-380: "Add context" reason dropdown order; "other" requires a note.
const CONTEXT_CLOSE_REASONS: CloseReasonCategory[] = [
  "lost_to_competitor",
  "project_cancelled",
  "no_progress",
  "other",
];

const CONTEXT_INPUT_CLASS =
  "mt-1 block w-full rounded border border-border bg-background p-2 text-sm transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

/** DD/MM/YYYY (Australian). Mirrors the BDM/PO DealExpiredContextCard. */
function formatAuDate(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

/** Inline "Add context" form body for the CM/RA/RM closed-lost card (CHP-380):
 * a "Reason for closure" dropdown, optional notes, Confirm/Cancel. Mirrors the
 * BDM/PO post-expiry card. The route supplies onSubmit (POST add-expiry-context
 * + refetch). The collapsed "Add context" trigger lives in the card's action
 * slot (designer review — same spot/size as the other feed-card CTAs); this
 * component renders only the expanded form, controlled by the caller via
 * `onCancel`. */
function ManagerExpiredContextForm({
  deal,
  onSubmit,
  onCancel,
  submitting,
}: {
  deal: ManagerDealListItem;
  onSubmit: (payload: {
    close_reason_category: CloseReasonCategory;
    close_notes: string | null;
  }) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [reason, setReason] = React.useState<CloseReasonCategory>("lost_to_competitor");
  const [notes, setNotes] = React.useState("");
  const notesRequired = reason === "other";

  return (
    <div
      className="w-full space-y-3 rounded border border-border p-3"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <div>
        <Label htmlFor={`mgr-reason-${deal.id}`}>Reason for closure *</Label>
        <select
          id={`mgr-reason-${deal.id}`}
          value={reason}
          onChange={(e) => setReason(e.target.value as CloseReasonCategory)}
          className={CONTEXT_INPUT_CLASS}
          disabled={submitting}
        >
          {CONTEXT_CLOSE_REASONS.map((r) => (
            <option key={r} value={r}>
              {CLOSE_REASON_LABELS[r]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor={`mgr-notes-${deal.id}`}>
          Additional details{notesRequired ? " *" : " (Optional)"}
        </Label>
        <Textarea
          id={`mgr-notes-${deal.id}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          rows={3}
          className="mt-1"
          disabled={submitting}
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={notesRequired && notes.trim().length === 0}
          loading={submitting}
          loadingLabel="Updating…"
          data-testid={`manager-feed-context-confirm-${deal.id}`}
          onClick={() =>
            onSubmit({ close_reason_category: reason, close_notes: notes.trim() || null })
          }
        >
          Confirm
        </Button>
        <Button size="sm" variant="ghost" disabled={submitting} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** The un-contexted, auto-expired closed-lost feed card (CHP-412): the richer
 * BDM/PO "DEAL EXPIRED — CLOSED-LOST" framing with the inline Add-context flow.
 *
 * Designer review: the collapsed "Add context" trigger lives in the card's
 * action slot — same spot + size (`sm`, outline) as every other feed-card CTA —
 * rather than buried in the card body. Expanding swaps the trigger for the full
 * reason/notes form in the body (it needs the full card width). */
function ManagerExpiredContextCard({
  deal,
  dismissLabel,
  submitting,
  onDismiss,
  onSubmit,
}: {
  deal: ManagerDealListItem;
  dismissLabel: string;
  submitting: boolean;
  onDismiss: () => void;
  onSubmit: (payload: {
    close_reason_category: CloseReasonCategory;
    close_notes: string | null;
  }) => void;
}) {
  const [open, setOpen] = React.useState(false);
  // Preserve the original null-token contract: a missing value hides the
  // " — $value" segment entirely (the lib formatter would render "—" instead).
  const value = deal.total_contract_value != null ? formatAud(deal.total_contract_value) : null;

  return (
    // CHP-412 / CHP-523: a passive (non-clickable) card on the coral "deal
    // expired" lane, mirroring the BDM/PO DealExpiredContextCard.
    <FeedCard
      tone="coral"
      icon={XCircle}
      eyebrow="DEAL EXPIRED — CLOSED-LOST"
      rawTestId={`manager-feed-card-${deal.id}`}
      // CHP-371: dismiss ✕ — auto-dismisses the card for the session.
      onDismiss={onDismiss}
      dismissLabel={dismissLabel}
      dismissTestId={`manager-feed-dismiss-${deal.id}`}
      title={
        <>
          {deal.client_name}
          {" — "}
          {deal.deal_id_display ?? "—"}
          {value ? ` — ${value}` : ""}
        </>
      }
      description={
        <>
          <p className="text-xs text-muted-foreground">{formatAuDate(deal.closed_at)}</p>
          {open ? (
            <div className="pt-2">
              <ManagerExpiredContextForm
                deal={deal}
                submitting={submitting}
                onSubmit={onSubmit}
                onCancel={() => setOpen(false)}
              />
            </div>
          ) : null}
        </>
      }
      // Designer review: "Add context" in the action slot, sized/styled like the
      // other feed CTAs. Hidden once the form is open (the form owns Confirm).
      action={
        open ? undefined : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpen(true)}
            data-testid={`manager-feed-add-context-${deal.id}`}
          >
            Add context
          </Button>
        )
      }
    />
  );
}

/** Per-variant card chrome (CHP-232 / CHP-222): the header label, the CTA, the
 * leading icon, and the FeedCard lane (CHP-523). Lane by kind: review items
 * (registration/extension/win) + the expiry-notice prompt need attention →
 * amber; the breach auto-approval is an emergency → coral; the purely-
 * informational closed-lost card → info. */
const KIND_COPY: Record<
  ManagerFeedItemKind,
  { label: string; cta: string; icon: LucideIcon; tone: FeedTone }
> = {
  registration: {
    label: "Registration for review",
    cta: "Review registration",
    icon: FileClock,
    tone: "amber",
  },
  extension: {
    label: "Extension for review",
    cta: "Review extension",
    icon: Clock,
    tone: "amber",
  },
  win: { label: "Closed-won for review", cta: "Review submission", icon: Trophy, tone: "amber" },
  // CHP-354: SLA-lapse auto-approval card — designer copy. Emergency lane.
  breach: {
    label: "Deal Registration Auto-Approved",
    cta: "Review outcome",
    icon: ShieldAlert,
    tone: "coral",
  },
  // CHP-336: informational only — no countdown. Info lane.
  closed_lost: { label: "Deal closed-lost", cta: "View deal", icon: XCircle, tone: "info" },
  // CHP-394: prompt to reach out before a registration lapses.
  expiry_notice: {
    label: "Deal expiry notice",
    cta: "View deal",
    icon: CalendarClock,
    tone: "amber",
  },
};

/** "expires in 1 day" / "expires in N days" (CHP-394 card body). */
function expiresPhrase(days: number): string {
  return `This deal registration expires in ${days} ${days === 1 ? "day" : "days"}`;
}

export interface ManagerFeedSidebarProps {
  /** Pre-classified to-do items (the caller runs `buildManagerFeed` against the
   * unified ledger dataset). */
  items: ManagerFeedItem[];
  /** Triggered by the per-card "Review …" button — caller navigates to the
   * full-page /dashboard/manager/deals/$dealId. */
  onReviewDeal: (dealId: string) => void;
  /** CHP-371: the per-card ✕ — caller dismisses the card (POST
   * dismiss-manager-card + refetch). */
  onDismiss: (dealId: string) => void;
  /** CHP-394: "Remind me in 7 days" on the expiry-notice card — caller POSTs
   * snooze-expiry-notice + refetch. Optional; the button is hidden when absent. */
  onSnooze?: (dealId: string) => void;
  /** CHP-380: "Add context" on an un-contexted closed-lost card — caller POSTs
   * add-expiry-context + refetch. Optional; the affordance is hidden when absent. */
  onAddExpiryContext?: (
    dealId: string,
    payload: { close_reason_category: CloseReasonCategory; close_notes: string | null },
  ) => void;
  /** CHP-380: the deal id whose add-context POST is in flight (drives the
   * Confirm button's "Updating…" state). */
  addingContextDealId?: string | null;
  /** Reference clock for countdown labels; defaults to `new Date()`. Pulled out
   * for deterministic tests. */
  now?: Date;
}

/** Sort tier for a feed item (lower = higher in the list):
 *   0 breach    — high-priority "Time Protection Triggered" alert.
 *   1 review    — actionable review items (registration / extension / win).
 *   2 closed_lost — informational only (CHP-336), below the to-dos.
 * Grouping review items into one tier ABOVE the informational tier keeps
 * deadline-less review cards (wins) ranked above purely-informational
 * closed-lost cards rather than interleaved by their missing deadline. */
function feedPriority(kind: ManagerFeedItem["kind"]): number {
  if (kind === "breach") return 0;
  if (kind === "closed_lost") return 2;
  return 1;
}

/** "Your Feed" for the manager dashboard.
 *
 * Renders to-do cards for every actionable review item — registration reviews
 * nearing auto-approval, all pending extension requests and closed-win
 * submissions (CHP-232), plus high-priority "Time Protection Triggered"
 * alerts for recent SLA-breach auto-approvals (deals-gap wave 2) and
 * informational "Deal closed-lost" alerts (CHP-336) — full-width above the
 * ledger (RACCPE-17). Each card carries variant-specific copy; items with an
 * auto-approval deadline show a countdown (wins don't auto-approve, so they
 * omit it). Collapses (returns null) when there are no items.
 *
 * CHP-321: a closed-win card carries no auto-approval deadline, so under a flat
 * five-item cap it could fall off the bottom in a busy pipeline and the CM never
 * saw the closed-won review despite the deal being in the ledger. The cap now
 * applies ONLY to surplus informational (closed-lost) cards — every actionable
 * to-do (breach + registration/extension/win reviews) is always visible, with
 * deadline-bearing cards ranked above deadline-less wins so the most urgent
 * registration is never displaced. The "see additional cards" affordance expands
 * the informational tail; nothing is silently clipped.
 */
export function ManagerFeedSidebar({
  items,
  onReviewDeal,
  onDismiss,
  onSnooze,
  onAddExpiryContext,
  addingContextDealId = null,
  now = new Date(),
}: ManagerFeedSidebarProps) {
  const [expanded, setExpanded] = React.useState(false);

  // Review-r2 (conventions sweep): an empty feed renders a calm zero-state
  // message rather than collapsing to nothing.
  if (items.length === 0) {
    return (
      <Card data-testid="manager-feed-empty">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Nothing needs your attention. Once a deal is registered updates will appear here.
        </CardContent>
      </Card>
    );
  }

  // Tiered priority (breach → review items → informational), then soonest
  // deadline first within a tier. CHP-321: deadline-less ACTIONABLE review
  // cards (closed-win submissions don't auto-approve, so they carry no SLA
  // deadline) sort FIRST within their tier rather than last — they never
  // self-resolve, so they must stay in the always-visible (first-five) set
  // instead of slipping behind a stack of deadline-bearing cards and off the
  // cap. Purely-informational cards (closed-lost) keep the Infinity key so
  // they stay below the deadline-bearing review items in their own tier.
  // Within a tier, soonest review deadline first (CHP-1176: win submissions
  // now carry a review-SLA clock too, so they compete on urgency like the
  // rest); the rare deadline-less card (a pre-CHP-1176 win submission) sorts
  // after the deadline-bearing ones.
  const sortKey = ({ deal }: ManagerFeedItem): number =>
    deal.effective_sla_deadline ? Date.parse(deal.effective_sla_deadline) : Infinity;
  const sorted = [...items].sort((a, b) => {
    const pa = feedPriority(a.kind);
    const pb = feedPriority(b.kind);
    if (pa !== pb) return pa - pb;
    return sortKey(a) - sortKey(b);
  });

  // CHP-321: NEVER collapse actionable to-dos (breach + registration/extension/
  // win reviews, feedPriority < 2) — a CM must see every pending action, and a
  // deadline-less win must not slip behind the cap. Only surplus informational
  // closed-lost cards fold behind the expand affordance. `sorted` is
  // priority-ordered, so the actionable cards are always the visible prefix.
  const actionableCount = sorted.filter((item) => feedPriority(item.kind) < 2).length;
  const collapsedCap = Math.max(MAX_FEED_ITEMS, actionableCount);
  const visible = expanded ? sorted : sorted.slice(0, collapsedCap);
  const hiddenCount = Math.max(0, items.length - collapsedCap);

  return (
    <aside className="space-y-3" aria-label="Your feed">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">Your Feed</h2>
        <Badge variant="secondary" data-testid="manager-feed-count">
          {items.length}
        </Badge>
      </div>

      <div className="grid gap-3">
        {visible.map(({ deal, kind }) => {
          const copy = KIND_COPY[kind];
          const Icon = copy.icon;
          // CHP-380: an auto-expired closed-lost deal with no recorded close
          // reason yet (expiry_context_added_at null) offers an inline "Add
          // context" form instead of the plain View-deal CTA. A *manually*
          // closed-lost deal already carries a close reason, so the affordance
          // must gate on auto_expired too — otherwise Confirm 409s (CHP-380).
          const needsContext =
            kind === "closed_lost" &&
            !!onAddExpiryContext &&
            deal.auto_expired === true &&
            !deal.expiry_context_added_at;
          // CHP-375: viewing a (purely informational, already-contexted)
          // closed-lost card also dismisses it — there's nothing to action.
          // An un-contexted card stays put so the manager can add the reason.
          const handleView = () => {
            onReviewDeal(deal.id);
            if (kind === "closed_lost" && !needsContext) onDismiss(deal.id);
          };

          // CHP-412: an un-contexted, auto-expired closed-lost card adopts the
          // richer BDM/PO DealExpiredContextCard framing — destructive tone,
          // "DEAL EXPIRED — CLOSED-LOST" header, the client–id–value lead line
          // and the close date, with the inline Add-context form below — rather
          // than the muted informational "Deal closed-lost" chrome. It is a
          // passive card (not a button): there's nothing to open until the
          // close reason is recorded, and the form must own its own clicks.
          if (needsContext && onAddExpiryContext) {
            return (
              <ManagerExpiredContextCard
                key={deal.id}
                deal={deal}
                dismissLabel={`Dismiss ${copy.label} for ${deal.client_name}`}
                submitting={addingContextDealId === deal.id}
                onDismiss={() => onDismiss(deal.id)}
                onSubmit={(payload) => onAddExpiryContext(deal.id, payload)}
              />
            );
          }

          return (
            <FeedCard
              key={deal.id}
              tone={copy.tone}
              icon={Icon}
              eyebrow={copy.label}
              rawTestId={`manager-feed-card-${deal.id}`}
              onClick={handleView}
              ariaLabel={`${copy.label} for ${deal.client_name}`}
              // CHP-371: dismiss ✕ — stops propagation so it doesn't open the deal.
              onDismiss={() => onDismiss(deal.id)}
              dismissLabel={`Dismiss ${copy.label} for ${deal.client_name}`}
              dismissTestId={`manager-feed-dismiss-${deal.id}`}
              title={deal.client_name}
              description={
                <>
                  {/* CHP-350: surface the partner's extension justification under
                      the client name on the extension review card. */}
                  {kind === "extension" && deal.extension_justification ? (
                    <p className="text-xs text-muted-foreground">
                      Reason: {deal.extension_justification}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {deal.deal_id_display ? <span>{deal.deal_id_display}</span> : null}
                    <span>{deal.channel_partner_company ?? "—"}</span>
                    {/* CHP-394: the expiry-notice sub-header names the deal owner
                        (BDM/PO) so the manager knows who to chase. */}
                    {kind === "expiry_notice" && deal.lead_bdm_name ? (
                      <span>{deal.lead_bdm_name}</span>
                    ) : null}
                    {kind === "breach" ? (
                      <span className="text-destructive">
                        Deal registration has been auto-approved without formal review.
                      </span>
                    ) : kind === "closed_lost" ? (
                      <span>Deal closed-lost — no action required</span>
                    ) : kind === "expiry_notice" && deal.target_close_date ? (
                      <span className="text-warning">
                        {expiresPhrase(daysUntilExpiry(deal.target_close_date, now))}
                      </span>
                    ) : deal.effective_sla_deadline ? (
                      <span className="text-warning">
                        {/* CHP-1176: wins never auto-approve — their card gets
                            the neutral review-SLA copy, not "Auto-approves". */}
                        {kind === "win"
                          ? formatReviewSlaCountdown(deal.effective_sla_deadline, now)
                          : formatSlaCountdown(deal.effective_sla_deadline, now)}
                      </span>
                    ) : null}
                  </div>
                </>
              }
              action={
                <div className="flex flex-wrap items-center gap-2">
                  {/* CHP-394: "Remind me in 7 days" — only the un-snoozed
                      14-day variant offers it; the 7-day card omits it. */}
                  {kind === "expiry_notice" && onSnooze && expiryNoticeCanSnooze(deal) ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSnooze(deal.id);
                      }}
                      data-testid={`manager-feed-snooze-${deal.id}`}
                    >
                      Remind me in 7 days
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView();
                    }}
                    data-testid={`manager-feed-review-${deal.id}`}
                  >
                    {copy.cta}
                  </Button>
                </div>
              }
            />
          );
        })}
        {hiddenCount > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            data-testid="manager-feed-expand-toggle"
            className="flex items-center justify-center gap-1 rounded-md py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                Show fewer
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                See {hiddenCount} more {hiddenCount === 1 ? "card" : "cards"}
              </>
            )}
          </button>
        ) : null}
      </div>
    </aside>
  );
}
